<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import AssistantAudienceForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantAudienceForm.vue';
import AssistantScheduleForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantScheduleForm.vue';
import LilithAccountConfiguration from './LilithAccountConfiguration.vue';
import { updateLilithAssistant } from 'dashboard/api/lilithTeam';
import {
  MAX_INACTIVITY_MINUTES,
  MIN_INACTIVITY_MINUTES,
  buildAudienceSettingsPatch,
  buildBasicSettingsPatch,
  buildScheduleSettingsPatch,
  buildSystemSettingsPatch,
} from './lilithSettings';

const props = defineProps({
  assistant: {
    type: Object,
    required: true,
  },
  canManage: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['updated']);

const { accountId } = useAccount();
const { t } = useI18n();
const store = useStore();

const activeSection = ref('basic');
const saving = ref(false);
const basic = reactive({
  name: '',
  description: '',
  productName: '',
  featureFaq: false,
  featureMemory: false,
  featureCitation: false,
  featureContactAttributes: false,
});
const system = reactive({
  handoffMessage: '',
  resolutionMessage: '',
  responseGuidelines: '',
  guardrails: '',
  autoResolveMode: 'evaluated',
  inactivityThresholdMinutes: 60,
  sendInactivityResolutionMessage: true,
});

const inactivityEnabled = computed(() => system.autoResolveMode !== 'disabled');
const inactivityThresholdValid = computed(() => {
  const value = Number(system.inactivityThresholdMinutes);
  return (
    Number.isInteger(value) &&
    value >= MIN_INACTIVITY_MINUTES &&
    value <= MAX_INACTIVITY_MINUTES
  );
});

const sections = computed(() => [
  { key: 'basic', label: t('TEAM_LILITH.SETTINGS.BASIC') },
  { key: 'system', label: t('TEAM_LILITH.SETTINGS.SYSTEM') },
  { key: 'audience', label: t('TEAM_LILITH.SETTINGS.AUDIENCE') },
  { key: 'schedule', label: t('TEAM_LILITH.SETTINGS.SCHEDULE') },
]);

const scheduleLabel = computed(() => {
  const value = props.assistant.config?.response_window || 'always';
  return t(`TEAM_LILITH.SETTINGS.RESPONSE_WINDOW.${value.toUpperCase()}`);
});

const audienceSummary = computed(() => {
  const audience = props.assistant.config?.audience;
  return audience
    ? JSON.stringify(audience, null, 2)
    : t('TEAM_LILITH.SETTINGS.AUDIENCE_EVERYONE');
});

const syncBasic = assistant => {
  const config = assistant?.config || {};
  basic.name = String(assistant?.name || '');
  basic.description = String(assistant?.description || '');
  basic.productName = String(config.product_name || '');
  basic.featureFaq = config.feature_faq === true;
  basic.featureMemory = config.feature_memory === true;
  basic.featureCitation = config.feature_citation === true;
  basic.featureContactAttributes = config.feature_contact_attributes === true;
};

const syncSystem = assistant => {
  const config = assistant?.config || {};
  system.handoffMessage = String(config.handoff_message || '');
  system.resolutionMessage = String(config.resolution_message || '');
  system.responseGuidelines = Array.isArray(assistant?.response_guidelines)
    ? assistant.response_guidelines.join('\n')
    : '';
  system.guardrails = Array.isArray(assistant?.guardrails)
    ? assistant.guardrails.join('\n')
    : '';
  system.autoResolveMode = String(config.auto_resolve_mode || 'evaluated');
  system.inactivityThresholdMinutes = Number(config.auto_resolve_after ?? 60);
  system.sendInactivityResolutionMessage =
    config.send_inactivity_resolution_message ?? true;
};

watch(
  () => props.assistant,
  assistant => {
    syncBasic(assistant);
    syncSystem(assistant);
  },
  { immediate: true, deep: true }
);

const savePatch = async patch => {
  if (!props.canManage || saving.value) return;
  saving.value = true;
  try {
    const response = await updateLilithAssistant(
      accountId.value,
      props.assistant.id,
      patch
    );
    emit('updated', response.data);
    useAlert(t('TEAM_LILITH.SETTINGS.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.response?.data?.detail ||
        error?.message ||
        t('TEAM_LILITH.SETTINGS.ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const saveBasic = () => {
  if (
    !basic.name.trim() ||
    !basic.description.trim() ||
    !basic.productName.trim()
  ) {
    return;
  }
  savePatch(buildBasicSettingsPatch(basic));
};

const saveSystem = () => {
  if (inactivityEnabled.value && !inactivityThresholdValid.value) return;
  savePatch(buildSystemSettingsPatch(system));
};

const saveAudience = payload =>
  savePatch(buildAudienceSettingsPatch(payload?.config?.audience ?? null));

const saveSchedule = payload =>
  savePatch(
    buildScheduleSettingsPatch(
      payload?.config?.response_window ||
        props.assistant.config?.response_window ||
        'always'
    )
  );

onMounted(() => {
  Promise.allSettled([
    store.dispatch('attributes/get'),
    store.dispatch('labels/get'),
  ]);
});
</script>

<template>
  <div class="flex flex-1 overflow-y-auto bg-n-surface-1">
    <div
      class="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-8 lg:px-10"
    >
      <header>
        <div
          class="text-xs font-semibold uppercase tracking-[0.12em] text-n-iris-10"
        >
          {{ $t('TEAM_LILITH.SETTINGS.EYEBROW') }}
        </div>
        <h1 class="mt-1 text-2xl font-semibold text-n-slate-12">
          {{ $t('TEAM_LILITH.SETTINGS.TITLE') }}
        </h1>
        <p class="mt-2 max-w-3xl text-sm leading-6 text-n-slate-11">
          {{ $t('TEAM_LILITH.SETTINGS.DESCRIPTION') }}
        </p>
        <p
          v-if="!canManage"
          class="mt-3 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-10"
        >
          {{ $t('TEAM_LILITH.SETTINGS.READ_ONLY') }}
        </p>
      </header>

      <nav class="flex gap-2 border-b border-n-weak pb-2">
        <button
          v-for="section in sections"
          :key="section.key"
          type="button"
          class="rounded-md px-3 py-2 text-sm font-medium"
          :class="
            activeSection === section.key
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12'
          "
          @click="activeSection = section.key"
        >
          {{ section.label }}
        </button>
      </nav>

      <section
        v-if="activeSection === 'basic'"
        class="flex flex-col gap-5 rounded-xl border border-n-weak bg-n-surface-2 p-5"
      >
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.SETTINGS.BASIC_TITLE') }}
          </h2>
          <p class="mt-1 text-sm leading-6 text-n-slate-11">
            {{ $t('TEAM_LILITH.SETTINGS.BASIC_DESCRIPTION') }}
          </p>
        </div>

        <Input
          v-model="basic.name"
          :label="$t('TEAM_LILITH.SETTINGS.ASSISTANT_NAME')"
          :disabled="!canManage"
        />
        <Input
          v-model="basic.productName"
          :label="$t('TEAM_LILITH.SETTINGS.PRODUCT_NAME')"
          :disabled="!canManage"
        />
        <TextArea
          v-model="basic.description"
          :label="$t('TEAM_LILITH.SETTINGS.ASSISTANT_DESCRIPTION')"
          :disabled="!canManage"
          :max-length="500"
          auto-height
        />

        <fieldset class="flex flex-col gap-2.5">
          <legend class="mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('TEAM_LILITH.SETTINGS.FEATURES') }}
          </legend>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input
              v-model="basic.featureFaq"
              type="checkbox"
              :disabled="!canManage"
            />
            {{ $t('TEAM_LILITH.SETTINGS.FEATURE_FAQ') }}
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input
              v-model="basic.featureMemory"
              type="checkbox"
              :disabled="!canManage"
            />
            {{ $t('TEAM_LILITH.SETTINGS.FEATURE_MEMORY') }}
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input
              v-model="basic.featureCitation"
              type="checkbox"
              :disabled="!canManage"
            />
            {{ $t('TEAM_LILITH.SETTINGS.FEATURE_CITATION') }}
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input
              v-model="basic.featureContactAttributes"
              type="checkbox"
              :disabled="!canManage"
            />
            {{ $t('TEAM_LILITH.SETTINGS.FEATURE_CONTACT_ATTRIBUTES') }}
          </label>
        </fieldset>

        <div v-if="canManage">
          <Button
            :label="$t('TEAM_LILITH.SETTINGS.SAVE_BASIC')"
            :is-loading="saving"
            :disabled="
              saving ||
              !basic.name.trim() ||
              !basic.description.trim() ||
              !basic.productName.trim()
            "
            @click="saveBasic"
          />
        </div>
      </section>

      <section
        v-else-if="activeSection === 'system'"
        class="flex flex-col gap-5 rounded-xl border border-n-weak bg-n-surface-2 p-5"
      >
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.SETTINGS.SYSTEM_TITLE') }}
          </h2>
          <p class="mt-1 text-sm leading-6 text-n-slate-11">
            {{ $t('TEAM_LILITH.SETTINGS.SYSTEM_DESCRIPTION') }}
          </p>
        </div>

        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ $t('TEAM_LILITH.SETTINGS.HANDOFF_MESSAGE') }}
          <textarea
            v-model="system.handoffMessage"
            rows="4"
            class="resize-y rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm font-normal text-n-slate-12 outline-none disabled:cursor-not-allowed disabled:opacity-60"
            :placeholder="$t('TEAM_LILITH.SETTINGS.HANDOFF_PLACEHOLDER')"
            :disabled="!canManage"
          />
        </label>

        <fieldset
          class="flex flex-col gap-3 rounded-lg border border-n-weak p-4"
        >
          <legend class="px-1 text-sm font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.SETTINGS.INACTIVITY_TITLE') }}
          </legend>
          <p class="text-sm leading-5 text-n-slate-11">
            {{ $t('TEAM_LILITH.SETTINGS.INACTIVITY_DESCRIPTION') }}
          </p>
          <label
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
          >
            {{ $t('TEAM_LILITH.SETTINGS.INACTIVITY_MODE') }}
            <select
              v-model="system.autoResolveMode"
              class="rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm font-normal text-n-slate-12 outline-none disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="!canManage"
            >
              <option value="evaluated">
                {{ $t('TEAM_LILITH.SETTINGS.INACTIVITY_MODE_EVALUATED') }}
              </option>
              <option value="legacy">
                {{ $t('TEAM_LILITH.SETTINGS.INACTIVITY_MODE_LEGACY') }}
              </option>
              <option value="disabled">
                {{ $t('TEAM_LILITH.SETTINGS.INACTIVITY_MODE_DISABLED') }}
              </option>
            </select>
          </label>
          <label
            v-if="inactivityEnabled"
            class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
          >
            {{ $t('TEAM_LILITH.SETTINGS.INACTIVITY_AFTER') }}
            <input
              v-model.number="system.inactivityThresholdMinutes"
              type="number"
              min="5"
              max="1440"
              step="5"
              class="rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm font-normal text-n-slate-12 outline-none disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="!canManage"
            />
            <span
              v-if="!inactivityThresholdValid"
              class="text-xs text-n-ruby-9"
            >
              {{ $t('TEAM_LILITH.SETTINGS.INACTIVITY_RANGE_ERROR') }}
            </span>
          </label>
          <label
            v-if="inactivityEnabled"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              v-model="system.sendInactivityResolutionMessage"
              type="checkbox"
              :disabled="!canManage"
            />
            {{ $t('TEAM_LILITH.SETTINGS.SEND_RESOLUTION_MESSAGE') }}
          </label>
        </fieldset>

        <label
          v-if="inactivityEnabled && system.sendInactivityResolutionMessage"
          class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
        >
          {{ $t('TEAM_LILITH.SETTINGS.RESOLUTION_MESSAGE') }}
          <textarea
            v-model="system.resolutionMessage"
            rows="4"
            class="resize-y rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm font-normal text-n-slate-12 outline-none disabled:cursor-not-allowed disabled:opacity-60"
            :placeholder="$t('TEAM_LILITH.SETTINGS.RESOLUTION_PLACEHOLDER')"
            :disabled="!canManage"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ $t('TEAM_LILITH.SETTINGS.RESPONSE_GUIDELINES') }}
          <textarea
            v-model="system.responseGuidelines"
            rows="6"
            maxlength="20000"
            class="resize-y rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm font-normal text-n-slate-12 outline-none disabled:cursor-not-allowed disabled:opacity-60"
            :placeholder="$t('TEAM_LILITH.SETTINGS.RULES_PLACEHOLDER')"
            :disabled="!canManage"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ $t('TEAM_LILITH.SETTINGS.GUARDRAILS') }}
          <textarea
            v-model="system.guardrails"
            rows="5"
            maxlength="20000"
            class="resize-y rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm font-normal text-n-slate-12 outline-none disabled:opacity-60"
            :placeholder="$t('TEAM_LILITH.SETTINGS.RULES_PLACEHOLDER')"
            :disabled="!canManage"
          />
        </label>
        <div v-if="canManage">
          <Button
            :label="$t('TEAM_LILITH.SETTINGS.SAVE_SYSTEM')"
            :is-loading="saving"
            :disabled="
              saving || (inactivityEnabled && !inactivityThresholdValid)
            "
            @click="saveSystem"
          />
        </div>
      </section>

      <section
        v-else-if="activeSection === 'audience'"
        class="flex flex-col gap-5 rounded-xl border border-n-weak bg-n-surface-2 p-5"
      >
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.SETTINGS.AUDIENCE_TITLE') }}
          </h2>
          <p class="mt-1 text-sm leading-6 text-n-slate-11">
            {{ $t('TEAM_LILITH.SETTINGS.AUDIENCE_DESCRIPTION') }}
          </p>
        </div>

        <AssistantAudienceForm
          v-if="canManage"
          :assistant="assistant"
          @submit="saveAudience"
        />
        <pre
          v-else
          class="overflow-x-auto whitespace-pre-wrap rounded-lg border border-n-weak bg-n-surface-1 p-4 text-xs leading-5 text-n-slate-11"
          >{{ audienceSummary }}</pre
        >
      </section>

      <section
        v-else
        class="flex flex-col gap-5 rounded-xl border border-n-weak bg-n-surface-2 p-5"
      >
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.SETTINGS.SCHEDULE_TITLE') }}
          </h2>
          <p class="mt-1 text-sm leading-6 text-n-slate-11">
            {{ $t('TEAM_LILITH.SETTINGS.SCHEDULE_DESCRIPTION') }}
          </p>
        </div>

        <AssistantScheduleForm
          v-if="canManage"
          :assistant="assistant"
          @submit="saveSchedule"
        />
        <div
          v-else
          class="rounded-lg border border-n-weak bg-n-surface-1 px-4 py-3 text-sm text-n-slate-11"
        >
          {{ scheduleLabel }}
        </div>
      </section>
    </div>
    <LilithAccountConfiguration :can-manage="canManage" />
  </div>
</template>
